var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "nativewind/jsx-runtime";
import { getMessaging } from '@react-native-firebase/messaging';
import { NavigationContainer, useNavigationContainerRef, } from '@react-navigation/native';
import React, { useCallback, useEffect, useRef } from 'react';
import { AppState, Linking, NativeModules, Platform } from 'react-native';
import Config from 'react-native-config';
import SplashScreen from 'react-native-splash-screen';
import { requestTrackingPermission } from 'react-native-tracking-transparency';
import { Airbridge } from 'airbridge-react-native-sdk';
import { getStorageValue } from '@/atoms/atomForLocal';
import DevTool from '@/components/DevTool/DevTool';
import { setDeferredDeepLinkUrl } from '@/deeplink/DeferredDeepLink';
import { setPendingSharedText } from '@/deeplink/PendingSharedText';
import { useLogParams } from '@/logging/LogParamsProvider';
import Logger from '@/logging/Logger';
import { Navigation } from '@/navigation';
import { DEEP_LINK_PREFIXES, linkingScreensConfig, webLinkingScreensConfig, } from '@/navigation/linkingConfig';
import { startupTiming } from '@/logging/startupTiming';
import { dismissSplashOverlay } from '@/splash/SplashOverlay';
import { classifyWebRoute } from '@/navigation/webAccess';
import { showAppInstallPrompt } from '@/utils/appInstallPrompt';
import { logDebug } from '@/utils/DebugUtils';
import { isAuthDeferred } from '@/utils/deepLinkUtils';
import HeatTelemetry from '@/utils/HeatTelemetry';
// 전체 화면 공용 로깅 규칙 정의
const ROUTE_PARAMS_LOGGING_RULES = {
    'placeInfo.placeId': 'place_id',
    'placeInfo.place.id': 'place_id',
    'placeInfo.name': 'place_name',
    placeListId: 'place_list_id', // PlaceListDetail screen_view 를 리스트별로 귀속 (튜토리얼·딥링크·탭 모든 진입 포함)
    // Webview screen_view 에 진입 URL 을 귀속. BI '검색' 지표의 웹콘텐츠 조회 판정
    // (bi_report.search_component) 이 이 값의 origin prefix 로 뿌클로드·scc-content 를
    // 카카오/네이버 지도·구글폼 웹뷰와 구분한다. url route param 을 가진 화면은 Webview 뿐.
    // Firebase 는 문자열 파라미터를 100자에서 자르지만 prefix 판정이라 무해하다.
    url: 'url',
    // 필요에 따라 추가...
};
// 중첩된 객체에서 특정 경로의 값을 가져오는 함수
const getNestedValue = (obj, path) => {
    return path.split('.').reduce((current, key) => current === null || current === void 0 ? void 0 : current[key], obj);
};
// 허용된 route params만 선택적으로 추출하는 함수
const extractAllowedRouteParams = (routeParams) => {
    const extracted = {};
    Object.entries(ROUTE_PARAMS_LOGGING_RULES).forEach(([paramPath, logKey]) => {
        const value = getNestedValue(routeParams, paramPath);
        if (value !== undefined && value !== null) {
            extracted[logKey] = value;
        }
    });
    return extracted;
};
const RootScreen = () => {
    var _a, _b;
    const routeNameRef = useRef(undefined);
    const navigationRef = useNavigationContainerRef();
    const globalLogParams = useLogParams();
    // __DEV__ only: expose navigationRef globally for CDP-based E2E testing
    React.useEffect(() => {
        if (!__DEV__) {
            return;
        }
        const g = globalThis;
        g.__navRef = navigationRef;
        return () => {
            delete g.__navRef;
        };
    }, [navigationRef]);
    // 외부 지도앱 공유 텍스트 수신 단일 진입점 (Android: ShareIntentModule / iOS: stair-crusher://shared?text=).
    // 안드/iOS × cold/warm 4개 경로가 전부 여기로 들어와 수신 시점에 1회 계측한다.
    // 비로그인 수신은 ResolvingSharedLink 를 안 거치므로 그 화면의 screen_view 로는 영영 안 잡힌다
    // — 로그인 게이트에서 몇 건이 죽는지 보려면 수신 시점 계측이 유일한 방법이다.
    const receiveSharedText = useCallback((sharedText, entry) => {
        var _a, _b;
        const isLoggedIn = !!getStorageValue('scc-token');
        Logger.logSharedTextReceived({ entry, isLoggedIn });
        // cold start 는 navigation 이 아직 미준비 → PendingSharedText 로 넘기고 MainScreen 이 소비한다.
        const isColdStart = entry === 'android_cold' || entry === 'ios_cold';
        if (isColdStart || !isLoggedIn) {
            setPendingSharedText(sharedText);
            if (!isColdStart) {
                ((_a = navigationRef.current) === null || _a === void 0 ? void 0 : _a.navigate)('Login');
            }
            return;
        }
        ((_b = navigationRef.current) === null || _b === void 0 ? void 0 : _b.navigate)('ResolvingSharedLink', {
            sharedText,
        });
    }, [navigationRef]);
    // 웹 라우트 게이트: 카메라 등록 플로우는 앱 설치 유도. 그 외 화면은 무로그인 열람 허용
    // (진입 시 익명 토큰 발행 — App.tsx). 익명 유저에겐 1일 1회 로그인 유도 팝업.
    // 초기 딥링크(onReady)와 이후 화면 전환(onStateChange) 양쪽에서 호출. 리다이렉트했으면 true 반환.
    const runWebRouteGate = useCallback((routeName) => {
        var _a, _b;
        if (Platform.OS !== 'web' || !routeName) {
            return false;
        }
        const access = classifyWebRoute(routeName);
        if (access === 'appOnly') {
            showAppInstallPrompt('이 기능은 앱에서만 이용할 수 있어요');
            if ((_a = navigationRef.current) === null || _a === void 0 ? void 0 : _a.canGoBack()) {
                navigationRef.current.goBack();
            }
            else {
                ((_b = navigationRef.current) === null || _b === void 0 ? void 0 : _b.navigate)('Main');
            }
            return true;
        }
        // 무로그인 열람 허용. 익명 유저 1일 1회 로그인 유도는 여기서 하지 않는다 —
        // web/components/DailyLoginPrompt.tsx(React 트리 렌더) 가 단일 소스다.
        // 과거엔 이 게이트에서도 showLoginPrompt(DOM 오버레이)를 띄웠는데, 쿼터 키가
        // 서로 달라(login-prompt-shown-date vs loginPromptLastShownDate) 둘 다 "오늘
        // 아직 안 띄웠다"고 판정해 팝업이 2개 겹쳤다.
        return false;
    }, [navigationRef]);
    // Android: MainActivity에서 직접 읽은 ACTION_SEND 텍스트를 NativeModule로 가져옴.
    // ReceiveSharingIntent 라이브러리는 getCurrentActivity() 타이밍 문제로 cold start 시 동작 안 함.
    useEffect(() => {
        if (Platform.OS !== 'android') {
            return;
        }
        const { ShareIntentModule } = NativeModules;
        // cold start: navigation 아직 미준비 → PendingSharedText 경유 (MainScreen이 소비)
        const checkOnColdStart = () => __awaiter(void 0, void 0, void 0, function* () {
            try {
                const text = yield (ShareIntentModule === null || ShareIntentModule === void 0 ? void 0 : ShareIntentModule.getPendingShareText());
                if (text) {
                    receiveSharedText(text, 'android_cold');
                }
            }
            catch (e) {
                logDebug('ShareIntentModule error', e);
            }
        });
        // background→foreground: navigation 이미 준비됨 → 바로 navigate
        const checkOnForeground = () => __awaiter(void 0, void 0, void 0, function* () {
            try {
                const text = yield (ShareIntentModule === null || ShareIntentModule === void 0 ? void 0 : ShareIntentModule.getPendingShareText());
                if (text) {
                    receiveSharedText(text, 'android_foreground');
                }
            }
            catch (e) {
                logDebug('ShareIntentModule error', e);
            }
        });
        checkOnColdStart();
        const subscription = AppState.addEventListener('change', state => {
            if (state === 'active') {
                checkOnForeground();
            }
        });
        return () => {
            subscription.remove();
        };
    }, [receiveSharedText]);
    return (_jsxs(_Fragment, { children: [_jsx(NavigationContainer, { ref: navigationRef, onReady: () => __awaiter(void 0, void 0, void 0, function* () {
                    var _a, _b;
                    // 콜드스타트 splash 종료 시점 계측 (진단용)
                    const navReady = Date.now();
                    Logger.logSplashDismissed({
                        jsToNavReadyMs: navReady - startupTiming.jsStart,
                        otaToNavReadyMs: startupTiming.otaCompleted
                            ? navReady - startupTiming.otaCompleted
                            : 0,
                    });
                    SplashScreen.hide();
                    dismissSplashOverlay();
                    if (Platform.OS === 'ios') {
                        requestTrackingPermission();
                    }
                    HeatTelemetry.beginSession().catch(() => { });
                    const currentScreenName = (_b = (_a = navigationRef.current) === null || _a === void 0 ? void 0 : _a.getCurrentRoute()) === null || _b === void 0 ? void 0 : _b.name;
                    // 초기 딥링크 게이트 (onStateChange는 초기 상태엔 발화하지 않음)
                    if (runWebRouteGate(currentScreenName)) {
                        return;
                    }
                    logDebug(`App starts at ${currentScreenName}`);
                    yield Logger.logScreenView({
                        currScreenName: currentScreenName,
                    });
                    routeNameRef.current = currentScreenName;
                }), linking: Platform.OS === 'web'
                    ? {
                        // 웹: 브라우저 URL ↔ 화면 동기화는 react-navigation이 자동 처리.
                        // 네이티브 deeplink/push/airbridge 로직은 불필요.
                        prefixes: [
                            (_b = (_a = globalThis.location) === null || _a === void 0 ? void 0 : _a.origin) !== null && _b !== void 0 ? _b : '',
                        ],
                        config: webLinkingScreensConfig,
                    }
                    : {
                        prefixes: DEEP_LINK_PREFIXES,
                        // 앱 quit 상태에서 deeplink 클릭이나 앱 푸시 클릭을 했을 때의 처리
                        getInitialURL: () => __awaiter(void 0, void 0, void 0, function* () {
                            var _a, _b, _c, _e, _f;
                            // iOS Share Extension cold start: Airbridge 가로채기보다 먼저 분기한다.
                            // background subscribe 경로와 동일한 우선순위 — 이 순서가 없으면 Airbridge의
                            // setOnDeeplinkReceived가 share URL을 먼저 먹어버려 PendingSharedText가 set되지 않는다.
                            const initialUrl = yield Linking.getInitialURL();
                            if (initialUrl === null || initialUrl === void 0 ? void 0 : initialUrl.startsWith('stair-crusher://shared?text=')) {
                                const sharedText = decodeURIComponent(initialUrl.replace('stair-crusher://shared?text=', ''));
                                logDebug('iOS Share Extension cold start', sharedText);
                                receiveSharedText(sharedText, 'ios_cold');
                                return null;
                            }
                            // Airbridge 디퍼드 딥링크 확인 (production만)
                            let resolvedUrl = null;
                            if (Config.FLAVOR === 'production') {
                                let resolved = false;
                                const airbridgeUrl = yield new Promise(resolve => {
                                    // 타임아웃 후 늦게 도착한 deeplink는 아래 else 분기에서
                                    // setDeferredDeepLinkUrl로 async 처리됨(MainScreen/HomeScreenV2가 소비)
                                    // → 짧게 잡아도 attribution 안 깨지고, 콜드스타트 splash 지연만 단축.
                                    // (3000ms → 800ms: prod 콜드스타트 ③ 구간이 3s 고정 대기였음, 계측으로 확인)
                                    const timeout = setTimeout(() => {
                                        resolved = true;
                                        resolve(null);
                                    }, 800);
                                    Airbridge.setOnDeeplinkReceived((deeplink) => {
                                        clearTimeout(timeout);
                                        if (deeplink.startsWith('kakao')) {
                                            return;
                                        }
                                        // iOS Share Extension URL은 위에서 이미 처리됨 — Airbridge가 다시 삼키지 않도록 무시
                                        if (deeplink.startsWith('stair-crusher://shared')) {
                                            return;
                                        }
                                        if (!resolved) {
                                            resolved = true;
                                            resolve(deeplink);
                                        }
                                        else {
                                            // ATT 응답 후 늦게 도착한 디퍼드 딥링크
                                            setDeferredDeepLinkUrl(deeplink);
                                        }
                                    });
                                });
                                if (airbridgeUrl) {
                                    logDebug('Airbridge deferred deeplink received', airbridgeUrl);
                                    resolvedUrl = airbridgeUrl;
                                }
                            }
                            // 일반 딥링크 처리 (share URL은 위에서 이미 처리됨)
                            if (!resolvedUrl && initialUrl) {
                                logDebug('Normal deeplink click during app quit state', initialUrl);
                                resolvedUrl = initialUrl;
                            }
                            // authDeferred=true → URL 저장 후 null 반환 (Main 마운트 후 소비)
                            if (resolvedUrl && isAuthDeferred(resolvedUrl)) {
                                logDebug('Auth-deferred deep link intercepted', resolvedUrl);
                                setDeferredDeepLinkUrl(resolvedUrl);
                                return null;
                            }
                            if (resolvedUrl) {
                                return resolvedUrl;
                            }
                            // 푸시 알림 처리
                            const message = yield getMessaging().getInitialNotification();
                            if (message) {
                                logDebug('getInitialNotification', message);
                                Logger.logAppPushOpen({
                                    title: ((_a = message.notification) === null || _a === void 0 ? void 0 : _a.title) || '',
                                    body: ((_b = message.notification) === null || _b === void 0 ? void 0 : _b.body) || '',
                                    campaignId: (_c = message.data) === null || _c === void 0 ? void 0 : _c.campaign_id,
                                    campaignType: (_e = message.data) === null || _e === void 0 ? void 0 : _e.campaign_type,
                                    serverPushLogId: (_f = message.data) === null || _f === void 0 ? void 0 : _f.server_push_log_id,
                                });
                                const data = message.data;
                                return data === null || data === void 0 ? void 0 : data._d;
                            }
                            return null;
                        }),
                        // 앱 background 상태에서 deeplink 클릭이나 앱 푸시 클릭을 했을 때의 처리
                        subscribe(listener) {
                            // 일반 딥링크 처리
                            const linkingSubscription = Linking.addEventListener('url', ({ url }) => {
                                var _a;
                                // 카카오 콜백 URL 무시 (iOS에서 Airbridge trackDeeplink가 처리하면서 딥링크 상태 방해)
                                if (url.startsWith('kakao')) {
                                    return;
                                }
                                // iOS Share Extension: stair-crusher://shared?text=<encoded>
                                if (url.startsWith('stair-crusher://shared?text=')) {
                                    const sharedText = decodeURIComponent(url.replace('stair-crusher://shared?text=', ''));
                                    receiveSharedText(sharedText, 'ios_warm');
                                    return;
                                }
                                // authDeferred=true + 비로그인 → URL 저장 + Login 리다이렉트
                                if (isAuthDeferred(url) &&
                                    !getStorageValue('scc-token')) {
                                    logDebug('Auth-deferred deep link (background)', url);
                                    setDeferredDeepLinkUrl(url);
                                    ((_a = navigationRef.current) === null || _a === void 0 ? void 0 : _a.navigate)('Login');
                                    return;
                                }
                                // 로그인 상태이거나 authDeferred 아님 → React Navigation에 위임
                                listener(url);
                            });
                            // 푸시 알림 처리
                            const pushSubscription = getMessaging().onNotificationOpenedApp((remoteMessage) => __awaiter(this, void 0, void 0, function* () {
                                var _a, _b, _c, _e, _f;
                                // https://github.com/invertase/react-native-firebase/issues/7749#issuecomment-2075084174
                                // 1. background 상태 -> 2. 앱 푸시 A 클릭해서 오픈 -> 3. quit 상태 -> 4. 앱 푸시 B 클릭해서 오픈
                                // 위와 같은 시나리오를 겪을 때, 4번의 getInitialNotification()에서 앱 푸시 A의 remoteMessage를 수신하는 문제가 있다.
                                // 링크된 github issue를 보면 2번 타이밍 때 getInitialNotification() 쪽에 앱 푸시 A에 대한 데이터가 로컬에 저장되는데,
                                // onNotificationOpenedApp() 호출로는 이 데이터가 초기화되지 않아서 4번 타이밍에 앱 푸시 A의 remoteMessage가 쓰이는 것으로 보인다.
                                // 임시 방편으로 2번 타이밍 때 getInitialNotification()를 호출해줘서 강제로 로컬에 저장된 remoteMessage를 초기화해준다.
                                getMessaging().getInitialNotification();
                                logDebug('onNotificationOpenedApp', remoteMessage);
                                Logger.logAppPushOpen({
                                    title: ((_a = remoteMessage.notification) === null || _a === void 0 ? void 0 : _a.title) || '',
                                    body: ((_b = remoteMessage.notification) === null || _b === void 0 ? void 0 : _b.body) || '',
                                    campaignId: (_c = remoteMessage.data) === null || _c === void 0 ? void 0 : _c.campaign_id,
                                    campaignType: (_e = remoteMessage.data) === null || _e === void 0 ? void 0 : _e.campaign_type,
                                    serverPushLogId: (_f = remoteMessage.data) === null || _f === void 0 ? void 0 : _f.server_push_log_id,
                                });
                                const data = remoteMessage.data;
                                if (data === null || data === void 0 ? void 0 : data._d) {
                                    listener(data._d);
                                }
                            }));
                            return () => {
                                linkingSubscription.remove();
                                pushSubscription();
                            };
                        },
                        config: linkingScreensConfig,
                    }, onStateChange: (state) => __awaiter(void 0, void 0, void 0, function* () {
                    var _a, _b, _c, _e;
                    const previousScreenName = routeNameRef.current;
                    const currentScreenName = (_b = (_a = navigationRef.current) === null || _a === void 0 ? void 0 : _a.getCurrentRoute()) === null || _b === void 0 ? void 0 : _b.name;
                    if (runWebRouteGate(currentScreenName)) {
                        return;
                    }
                    if (previousScreenName !== currentScreenName) {
                        logDebug(`Screen ${previousScreenName} -> ${currentScreenName}`);
                        const routeParams = (_e = (_c = state === null || state === void 0 ? void 0 : state.routes.at(-1)) === null || _c === void 0 ? void 0 : _c.params) !== null && _e !== void 0 ? _e : {};
                        const allowedRouteParams = extractAllowedRouteParams(routeParams);
                        yield Logger.logScreenView({
                            prevScreenName: previousScreenName,
                            currScreenName: currentScreenName,
                            extraParams: Object.assign(Object.assign({}, globalLogParams), allowedRouteParams),
                        });
                        routeNameRef.current = currentScreenName;
                    }
                }), children: _jsx(Navigation, {}) }), _jsx(DevTool, {})] }));
};
export default RootScreen;
